import type { FC, ReactNode } from 'react';
import React from 'react';
import Collapse from 'react-bootstrap/Collapse';
import settingsIcon from '../../../assets/images/Private_Banking/setting.svg';
import { getBankDomicileColumnLabel } from '../../../util/fileByApi';
import type { GroupedData, MergedRowDataWithLast, PrivateBankingRow } from '../types';
import type { FilterState } from './FilterPanel';

const renderBankDomicileHeaderLabel = (apiProductCode?: string | null): ReactNode => {
    const [firstLine, secondLine] = getBankDomicileColumnLabel(apiProductCode).split(' ');
    return (
        <>
            {firstLine}
            <br />
            {secondLine}
        </>
    );
};

// Column width constants (kept in sync with main report)
const COLUMN_WIDTHS = {
    FILTER_HEADER: '250px',
    QUALIFICATION: '100px',
    INDICATOR: '70px',
    COMMENT: '300px',
} as const;

// Helper component to render colgroup (reduces code duplication)
const TableColGroup: FC<{ filters: FilterState }> = ({ filters }) => (
    <colgroup>
        <col className="filter-header-cell" style={{ width: COLUMN_WIDTHS.FILTER_HEADER }} />
        <col className="qualification-col" style={{ width: COLUMN_WIDTHS.QUALIFICATION }} />
        {filters.activeClientDomicile && (
            <col className="indicator-cell" style={{ width: COLUMN_WIDTHS.INDICATOR }} />
        )}
        {filters.activeCrossBorder && (
            <col className="indicator-cell" style={{ width: COLUMN_WIDTHS.INDICATOR }} />
        )}
        {filters.activeBankDomicile && (
            <col className="indicator-cell" style={{ width: COLUMN_WIDTHS.INDICATOR }} />
        )}
        {filters.passiveClientDomicile && (
            <col className="indicator-cell" style={{ width: COLUMN_WIDTHS.INDICATOR }} />
        )}
        {filters.passiveCrossBorder && (
            <col className="indicator-cell" style={{ width: COLUMN_WIDTHS.INDICATOR }} />
        )}
        {filters.passiveBankDomicile && (
            <col className="indicator-cell" style={{ width: COLUMN_WIDTHS.INDICATOR }} />
        )}
        {filters.showComments && (
            <col className="comment-header-cell" style={{ width: COLUMN_WIDTHS.COMMENT }} />
        )}
    </colgroup>
);

interface PrivateBankingTableProps {
    apiProductCode?: string | null;
    filters: FilterState;
    activeColumnsCount: number;
    passiveColumnsCount: number;
    totalDataColumns: number;
    isLoading: boolean;
    renderSkeletonLoader: () => React.ReactNode;
    groupedData: Array<GroupedData>;
    expandedSections: Set<string>;
    toggleSection: (sectionKey: string) => void;
    processRowsForMerging: (rows: Array<PrivateBankingRow>) => Array<MergedRowDataWithLast>;
    renderRow: (mergedData: MergedRowDataWithLast) => React.ReactNode;
    setShowFilterPanel: (show: boolean) => void;
}

const PrivateBankingTable: FC<PrivateBankingTableProps> = ({
    apiProductCode,
    filters,
    activeColumnsCount,
    passiveColumnsCount,
    totalDataColumns,
    isLoading,
    renderSkeletonLoader,
    groupedData,
    expandedSections,
    toggleSection,
    processRowsForMerging,
    renderRow,
    setShowFilterPanel,
}) => {
    return (
        <div className="private-banking-table-container">
            <table className="table private-banking-table private-banking-table-no-border">
                <TableColGroup filters={filters} />
                {/* Sticky Header */}
                <thead className="stickyTblHead">
                    <tr>
                        <th className="no-border"></th>
                        <th className="no-border"></th>
                        {activeColumnsCount > 0 && (
                            <th
                                colSpan={activeColumnsCount}
                                className={`text-center active-passive-header active-header ${passiveColumnsCount === 0 ? 'single-active-header' : ''}`}
                            >
                                Active
                            </th>
                        )}
                        {passiveColumnsCount > 0 && (
                            <th
                                colSpan={passiveColumnsCount}
                                className={`text-center active-passive-header passive-header ${activeColumnsCount === 0 ? 'single-passive-header' : ''}`}
                            >
                                Passive
                            </th>
                        )}
                        {filters.showComments && <th className="comment-header-cell"></th>}
                    </tr>

                    <tr className="qualification-header-row">
                        <th className="filter-header-cell">
                            <button
                                className="btn filter-symbol-button-inline"
                                onClick={() => setShowFilterPanel(true)}
                                title="Filter Settings"
                            >
                                <img src={settingsIcon} alt="settings" className="settings-icon" />
                                <span className="ms-2 clColSettings">Column settings</span>
                            </button>
                        </th>
                        <th className="qualification-col">Qualification</th>
                        {filters.activeClientDomicile && (
                            <th>
                                <div
                                    className={
                                        filters.showComments
                                            ? 'qualification-header-cell-content'
                                            : 'qualification-header-cell-content no-badge'
                                    }
                                >
                                    <div>
                                        Client
                                        <br />
                                        Domicile
                                    </div>
                                    {filters.showComments && (
                                        <div>
                                            <span className="header-badge header-badge-active">1</span>
                                        </div>
                                    )}
                                </div>
                            </th>
                        )}
                        {filters.activeCrossBorder && (
                            <th>
                                <div
                                    className={
                                        filters.showComments
                                            ? 'qualification-header-cell-content'
                                            : 'qualification-header-cell-content no-badge'
                                    }
                                >
                                    <div>
                                        Cross-
                                        <br />
                                        border
                                    </div>
                                    {filters.showComments && (
                                        <div>
                                            <span className="header-badge header-badge-active">2</span>
                                        </div>
                                    )}
                                </div>
                            </th>
                        )}
                        {filters.activeBankDomicile && (
                            <th>
                                <div
                                    className={
                                        filters.showComments
                                            ? 'qualification-header-cell-content'
                                            : 'qualification-header-cell-content no-badge'
                                    }
                                >
                                    <div>{renderBankDomicileHeaderLabel(apiProductCode)}</div>
                                    {filters.showComments && (
                                        <div>
                                            <span className="header-badge header-badge-active">3</span>
                                        </div>
                                    )}
                                </div>
                            </th>
                        )}
                        {filters.passiveClientDomicile && (
                            <th>
                                <div
                                    className={
                                        filters.showComments
                                            ? 'qualification-header-cell-content'
                                            : 'qualification-header-cell-content no-badge'
                                    }
                                >
                                    <div>
                                        Client
                                        <br />
                                        Domicile
                                    </div>
                                    {filters.showComments && (
                                        <div>
                                            <span className="header-badge header-badge-passive">4</span>
                                        </div>
                                    )}
                                </div>
                            </th>
                        )}
                        {filters.passiveCrossBorder && (
                            <th>
                                <div
                                    className={
                                        filters.showComments
                                            ? 'qualification-header-cell-content'
                                            : 'qualification-header-cell-content no-badge'
                                    }
                                >
                                    <div>
                                        Cross-
                                        <br />
                                        border
                                    </div>
                                    {filters.showComments && (
                                        <div>
                                            <span className="header-badge header-badge-passive">5</span>
                                        </div>
                                    )}
                                </div>
                            </th>
                        )}
                        {filters.passiveBankDomicile && (
                            <th>
                                <div
                                    className={
                                        filters.showComments
                                            ? 'qualification-header-cell-content'
                                            : 'qualification-header-cell-content no-badge'
                                    }
                                >
                                    <div>{renderBankDomicileHeaderLabel(apiProductCode)}</div>
                                    {filters.showComments && (
                                        <div>
                                            <span className="header-badge header-badge-passive">6</span>
                                        </div>
                                    )}
                                </div>
                            </th>
                        )}
                        {filters.showComments && (
                            <th
                                className="comment-header-cell"
                                style={{ borderTopRightRadius: '16px' }}
                            >
                                Comments
                            </th>
                        )}
                    </tr>
                </thead>

                {isLoading ? (
                    renderSkeletonLoader()
                ) : (
                    <tbody>
                        {groupedData.map((groupData) => (
                            <React.Fragment key={groupData.group}>
                                <tr>
                                    <td
                                        colSpan={totalDataColumns}
                                        style={{ height: '30px' }}
                                    ></td>
                                </tr>
                                {/* Group Header - plain heading, not collapsible */}
                                <tr className="group-section-header-row">
                                    <td
                                        colSpan={totalDataColumns}
                                        className="group-section-header"
                                    >
                                        <h3 className="group-section-header-text">{groupData.group}</h3>
                                    </td>
                                </tr>

                                {groupData.subGroups.map((subGroupData) => {
                                    const sectionKey = `${groupData.group}-${subGroupData.subGroup}`;
                                    const isExpanded = expandedSections.has(sectionKey);
                                    const processedRows = processRowsForMerging(subGroupData.rows);

                                    return (
                                        <React.Fragment key={sectionKey}>
                                            <tr>
                                                <td
                                                    colSpan={totalDataColumns}
                                                    style={{ height: '16px' }}
                                                ></td>
                                            </tr>
                                            {/* SubGroup Card - each service group is its own collapsible card */}
                                            <tr>
                                                <td
                                                    colSpan={totalDataColumns}
                                                    className="subgroup-card-cell"
                                                >
                                                    <div className="subgroup-card">
                                                        <div
                                                            className="subgroup-card-header"
                                                            onClick={() => toggleSection(sectionKey)}
                                                            aria-expanded={isExpanded}
                                                        >
                                                            <h4 className="subsection-header">{subGroupData.subGroup}</h4>
                                                            {/* One icon rotated by CSS (not a chevron-right/-down class swap) so the turn animates. */}
                                                            <i
                                                                className={`bi bi-chevron-right subgroup-card-chevron${
                                                                    isExpanded ? ' is-expanded' : ''
                                                                }`}
                                                                aria-hidden="true"
                                                            ></i>
                                                        </div>
                                                        <Collapse in={isExpanded}>
                                                            <div>
                                                                <table
                                                                    className="table private-banking-table private-banking-table-no-border"
                                                                    style={{
                                                                        width: '100%',
                                                                        borderCollapse: 'separate',
                                                                        borderSpacing: 0,
                                                                    }}
                                                                >
                                                                    <TableColGroup filters={filters} />
                                                                    <tbody>
                                                                        {processedRows.map((mergedData) =>
                                                                            renderRow(mergedData),
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </Collapse>
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </tbody>
                )}
            </table>
        </div>
    );
};

export default PrivateBankingTable;

